<template>
  <button
    :class="{
      [color_class]: true,
      'button--big': big,
      'button--small': small,
      'button--icon-only': !text && !loading && !big,
      [disabled_class]: disabled,
    }"
    :disabled="disabled || loading"
    class="button"
    :style="button_style"
  >
    <svg-element
      v-if="icon && !loading"
      :icon="icon"
      :size="small ? 10 : 16"
    />

    <svg-element
      v-if="loading"
      icon="loader"
      :size="small ? 10 : 16"
    />

    <div v-if="text">
      <span
        :class="{
          'nds-text-link': big,
          'nds-text-button-small-bold': small,
          'nds-text-link-small': !big && !small,
        }"
      >{{ text }}</span>
    </div>
  </button>
</template>

<script>
import SvgElement from '@/components/elements/SvgElement.vue';

export default {
  name: 'ButtonElement',
  components: {
    SvgElement,
  },
  props: {
    big: {
      default: false,
      type: Boolean,
    },
    color: String,
    disabled: {
      default: false,
      type: Boolean,
    },
    icon: String,
    loading: {
      default: false,
      type: Boolean,
    },
    small: {
      default: false,
      type: Boolean,
    },
    text: String,
    width: {
      type: String,
    },
  },
  computed: {
    button_style() {
      return this.width ? { width: this.width } : { 'white-space': 'nowrap' };
    },
    color_class() {
      switch (this.color) {
      case 'blue-o':
        return 'button--blue-o';

      case 'red':
        return 'button--red';

      case 'red-o':
        return 'button--red-o';

      case 'green':
        return 'button--green';

      case 'green-o':
        return 'button--green-o';

      case 'grey-o':
        return 'button--grey-o';

      case 'ai':
        return 'button--ai';

      default:
        return 'button--blue';
      }
    },
    disabled_class() {
      switch (this.color) {
      case 'blue-o':
      case 'green-o':
      case 'red-o':
        return 'button--disabled-o';

      default:
        return 'button--disabled';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/constants/variables.module.scss';

.button {
  $p: &;

  align-items: center;
  border-radius: 8px;
  display: inline-flex;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  gap: 8px;
  height: 32px;
  letter-spacing: -0.14px;
  line-height: 18px;
  justify-content: center;
  padding: 6px 12px;

  &--big {
    border-radius: 12px;
    font-size: 16px;
    height: 36px;
    letter-spacing: -0.16px;
    line-height: 22px;
    padding: 8px 12px;
  }

  &--small {
    border-radius: 4px;
    font-size: 10px;
    height: 22px;
    letter-spacing: -0.1px;
    line-height: 12px;
    padding: 4px 8px;
    gap: 4px;
  }

  &--icon-only {
    font-size: 0;
    height: 32px;
    justify-content: center;
    padding: 0;
    width: 32px;

    &#{$p}--small {
      height: 20px;
      width: 20px;
    }
  }

  &--blue {
    background: $cornflower-blue;
    border: 1px solid $cornflower-blue;
    color: $white;

    span {
      color: inherit;
    }

    svg {
      --color: #{$white};
    }

    &:not(:disabled):hover {
      background: $medium-slate-blue;
      border: 1px solid $medium-slate-blue;
    }
  }

  &--blue-o {
    border: 1px solid $cornflower-blue;
    color: $cornflower-blue;

    span {
      color: inherit;
    }

    svg {
      --color: #{$cornflower-blue};
    }

    &:not(:disabled):hover {
      border: 1px solid $medium-slate-blue;
      color: $medium-slate-blue;

      span {
        color: inherit;
      }

      svg {
        --color: #{$medium-slate-blue};
      }
    }
  }

  &--red {
    background: $geraldine;
    border: 1px solid $geraldine;
    color: $white;

    span {
      color: inherit;
    }

    svg {
      --color: #{$white};
    }

    &:not(:disabled):hover {
      background: $deep-blush;
      border: 1px solid $deep-blush;
    }
  }

  &--red-o {
    border: 1px solid $geraldine;
    color: $geraldine;

    span {
      color: inherit;
    }

    svg {
      --color: #{$geraldine};
    }

    &:not(:disabled):hover {
      border: 1px solid $deep-blush;
      color: $deep-blush;

      span {
        color: inherit;
      }

      svg {
        --color: #{$deep-blush};
      }
    }
  }

  &--green {
    background: $salem;
    border: 1px solid $salem;
    color: $white;

    svg {
      --color: #{$white};
    }
  }

  &--green-o {
    border: 1px solid $jade;
    color: $jade;

    span {
      color: inherit;
    }

    svg {
      --color: #{$jade};
    }

    &:not(:disabled):hover {
      border: 1px solid $salem;
      color: $salem;

      span {
        color: inherit;
      }

      svg {
        --color: #{$salem};
      }
    }
  }

  &--ai {
    $ai-from: var(--Tribal-Purple, #7841BC);
    $ai-to: var(--Unique-Purple, #7E77E3);

    background:
      linear-gradient(90deg, $ai-from 0%, $ai-to 100%) padding-box,
      linear-gradient(90deg, $ai-from 0%, $ai-to 100%) border-box;
    border: 1px solid transparent;
    color: $white;
    overflow: hidden;

    span {
      color: inherit;
    }

    svg {
      --color: #{$white};
    }

    &:not(:disabled):hover {
      background: $ai-to;
    }
  }

  &--grey-o {
    border: 1px solid $amethyst-smoke;
    color: $amethyst-smoke;

    svg {
      --color: #{$amethyst-smoke};
    }

    &:not(:disabled):hover {
      border: 1px solid $amethyst-smoke;
      color: $amethyst-smoke;

      svg {
        --color: #{$amethyst-smoke};
      }
    }
  }

  &--disabled {
    background: $medium-gray;
    border: 1px solid $medium-gray;
    color: $white;
    cursor: not-allowed;

    span {
      color: inherit;
    }

    svg {
      --color: #{$white};
    }

    &:hover {
      background: $medium-gray;
      border: 1px solid $medium-gray;
      color: $white;

      svg {
        --color: #{$white};
      }
    }
    background-color: $medium-gray;
    border: 1px solid $medium-gray;
    color: $white;
    cursor: not-allowed !important;
  }

  &--disabled-o {
    border: 1px solid $amethyst-smoke;
    background-color: $white;
    color: $amethyst-smoke;
    cursor: not-allowed !important;

    span {
      color: inherit;
    }
  }
}
</style>
